import { UserFindDto, type UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { PacsDtoKeysEnum } from '@zaoblako/shared/modules/pacs/enums/pacs-dto-keys.enum';
import { CatalogDtosKeysEnum } from '@zaoblako/shared/modules/catalog/enums/catalog-dtos-keys.enum';
import type { RoomFindInterface } from '@zaoblako/shared/modules/catalog/dtos/room/room-find.dto';

import * as yup from 'yup';

/** "NetPing" device response body - DTO signature */
export interface NetPingDeviceFindInterface {
  /** Unique identifier. Cannot be changed. */
  id: number;

  // Tech fields
  /** Creation date and time */
  created: Date;
  /** Last update date and time */
  updated: Date;
  /** Soft deletion update date and time. The device will be permanently deleted six months after this date. */
  deleted?: Date;
  /** User who created this device */
  createdBy?: UserFindInterface;

  // Data fields
  /** Device display name */
  name: string;
  /** Device IP-address */
  ipAddress: string;
  /** Device connection UDP port numbe */
  udpPort: number;
  /** Community line for the barrier opening channel */
  barrierCommunity: string;
  /** OID of the channel through which the barrier is opened */
  barrierOpenRelayOid: string;
  /** The value that must be sent to the channel to open the barrier */
  barrierOpenRelayValue: number;
  /** OID of the channel through which the barrier status is read */
  barrierStatusRelayOid: string;
  /** The value at which the barrier should be considered open */
  barrierStatusRelayOpenValue: number;
  /** Open only a closed barrier */
  barrierOpenOnlyClosed: boolean;
  /** Delay between attempts to open the barrier in seconds */
  barrierOpenAttemptDelay: number;
  /** Number of attempts to open the barrier */
  barrierOpenAttemptsCount: number;
  /** The room that is access controlled by this device */
  room?: RoomFindInterface;
}

/** "NetPing" device response body - DTO schema */
export const NetPingDeviceFindDto = SerdeDtoClass.create<NetPingDeviceFindInterface>(PacsDtoKeysEnum.NET_PING_DEVICE_FIND_DTO, {
  id: {
    sortable: true,
    label: 'Номер',
    serializer: yup.number(),
  },
  created: {
    sortable: true,
    label: 'Создан',
    serializer: yup.date(),
  },
  updated: {
    sortable: true,
    label: 'Изменен',
    serializer: yup.date(),
  },
  deleted: {
    optional: true,
    label: 'Удален',
    serializer: yup.date(),
  },
  createdBy: {
    sortable: true,
    optional: true,
    label: 'Автор',
    nested: UserFindDto,
    serializer: (_, nested) => nested,
  },
  name: {
    sortable: true,
    label: 'Название',
    serializer: yup.string(),
  },
  ipAddress: {
    sortable: true,
    label: 'IP-адрес устройства',
    serializer: yup.string(),
  },
  udpPort: {
    label: 'UDP-порт подключения устройства',
    serializer: yup.number(),
  },
  barrierCommunity: {
    label: 'Community-строка устройства',
    serializer: yup.string(),
  },
  barrierOpenRelayOid: {
    label: 'OID канала, по которому открывается шлагбаум',
    serializer: yup.string(),
  },
  barrierOpenRelayValue: {
    label: 'Значение, которое надо отправить в канал для открытия шлагбаума',
    serializer: yup.number(),
  },
  barrierStatusRelayOid: {
    label: 'OID канала, по которому считывается состояние шлагбаума',
    serializer: yup.string(),
  },
  barrierStatusRelayOpenValue: {
    label: 'Значение, при котором шлагбаум следует считать открытым',
    serializer: yup.number(),
  },
  barrierOpenOnlyClosed: {
    label: 'Открывать только закрытый шлагбаум',
    hint: (
      'Если включено, то перед отправкой сигнала на открытие будет проверяться, не открыт ли уже шлагбаум. ' +
      'Если шлагбаум открыт, то ФРМ будет ждать, пока шлагбаум не закроется или пока не закончатся попытки открыть шлагбаум.'
    ),
    serializer: yup.boolean(),
  },
  barrierOpenAttemptDelay: {
    label: 'Интервал между попытками открыть шлагбаум в секундах',
    serializer: yup.number(),
  },
  barrierOpenAttemptsCount: {
    label: 'Максимальное количество попыток открыть шлагбаум',
    serializer: yup.number(),
  },
  room: {
    sortable: true,
    optional: true,
    label: 'Помещение',
    hint: 'Помещение, доступ в которое контролируется этим устройством',
    nested: CatalogDtosKeysEnum.ROOM_FIND_DTO,
    serializer: (_, nested) => nested,
  },
});
